import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { IMyDevice } from 'matrix-js-sdk';
import { DeviceVerificationStatus } from 'matrix-js-sdk/lib/crypto-api';
import { MatrixService } from '../matrix/matrix.service';
import { VerificationService } from '../verification/verification.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Mit dieser Komponente wird das DeviceManagement für einzelne SB-User umgesetzt. Speziell wird hier
 * die Liste der Devices angezeigt.
 * Hierdurch erfüllte AFOs:
 * A_25395-03 - Matrix Module (Device Management)
 */
export class DeviceService {
  private readonly clientService = inject(MatrixService);
  private readonly verificationService = inject(VerificationService);

  private readonly refreshDevices$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public refreshDevices(): void {
    this.refreshDevices$.next(true);
  }

  public async isVerified(deviceId: string): Promise<DeviceVerificationStatus | null> {
    const userId = this.clientService.client.getUserId();
    if (!userId) {
      throw new Error('Keine UserId im Client vorhanden!');
    }
    const crypto = this.clientService.client.getCrypto();
    if (!crypto) {
      throw new Error('Keine Crypto-API vorhanden');
    }
    return await crypto.getDeviceVerificationStatus(userId, deviceId);
  }

  public getActiveDevice(): string | null {
    return this.clientService.client.deviceId;
  }

  public getDevices$(): Observable<IMyDevice[]> {
    return this.refreshDevices$.asObservable().pipe(
      switchMap(async () => {
        const devices = await this.clientService.client.getDevices();
        return devices.devices.sort((a, b) => (b.last_seen_ts ?? 0) - (a.last_seen_ts ?? 0));
      })
    );
  }

  public async deleteDevice(deviceId: string): Promise<void> {
    try {
      await this.clientService.client.deleteDevice(deviceId);
      this.refreshDevices();
    } catch (error: any) {
      if (error.httpStatus === 401) {
        await this.verificationService.openReAuth(error.data.session);
        await this.clientService.client.deleteDevice(deviceId, { session: error.data.session });
        this.refreshDevices();
      } else {
        throw error;
      }
    }
  }

  public async setDeviceDetails(deviceId: string, name: string): Promise<void> {
    await this.clientService.client.setDeviceDetails(deviceId, {
      display_name: name
    });
    this.refreshDevices();
  }
}
